import { createSelector } from '@reduxjs/toolkit';

import type { AssetsAssetSchemaTypedWithIdNullable } from '@iconik-io/iconik-api/src/services/assets';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import {
  isPermission,
  type SearchItemEntity,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';

import {
  selectAssetFiles,
  selectAssetFormats,
  selectAssetProxies,
} from '../../ducks/pages/asset/selectors';
import type { AppState } from '../../store/types';

const getAsset = (
  _state: AppState,
  asset: AssetsAssetSchemaTypedWithIdNullable,
) => asset;

const getAcls = (
  state: AppState,
  asset: AssetsAssetSchemaTypedWithIdNullable,
  isShare: boolean,
) =>
  isShare ? { read: true } : state.acls.assets.entityCurrentUserAcls[asset.id];

const getProxies = (
  state: AppState,
  asset: AssetsAssetSchemaTypedWithIdNullable,
  _isShare: boolean,
  versionId?: string,
) => selectAssetProxies(state, asset.id, versionId);

const getFormats = (
  state: AppState,
  asset: AssetsAssetSchemaTypedWithIdNullable,
  _isShare: boolean,
  versionId?: string,
) => selectAssetFormats(state, asset.id, versionId);

const getFiles = (
  state: AppState,
  asset: AssetsAssetSchemaTypedWithIdNullable,
  _isShare: boolean,
  versionId?: string,
) => selectAssetFiles(state, asset.id, versionId);

export const selectAssetAsSearchItemEntity = createSelector(
  [getAsset, getAcls, getProxies, getFormats, getFiles],
  (asset, acls, proxies, formats, files) => {
    let result: SearchItemEntity | undefined;

    if (acls) {
      const permissions = Object.entries(acls).flatMap(
        ([permission, allowed]) =>
          allowed && isPermission(permission) ? [permission] : [],
      );
      result = {
        ...asset,
        object_type: OBJECT_TYPE_ASSETS,
        permissions,
        proxies,
        formats,
        files,
      };
    }

    return result;
  },
);
